import type { PolaroidMemory } from "@aybuben/client/device/polaroid-memory.ts";
import { placeCardsOf } from "@aybuben/client/places/cards.ts";
import {
	chipColumns,
	homeSections,
	unitHeader,
	type FocusRequest,
	type HomeSection,
	type HomeUnit,
} from "@aybuben/client/screens/home.ts";
import { MIN_WIDTH } from "@aybuben/client/screens/trail.ts";
import { type UnitView } from "@aybuben/core/content/course.ts";
import type { LessonDef, ProgressKey } from "@aybuben/core/content/types.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { Crown } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type Ref } from "react";

import { useCourse } from "../content/context.tsx";
import { FaceView } from "../exercises/kit.tsx";
import { useStrings } from "../i18n/index.tsx";
import { COL, FOCUS, UI } from "../theme.ts";
import { reducedMotion } from "./motion.ts";
import { PhotoSpot } from "./places/PhotoSpot.tsx";
import { PlaceCard } from "./places/PlaceCard.tsx";
import { G } from "./primitives.tsx";
import { TRACK_UI } from "./track-ui.tsx";
import { JumpButton } from "./trail/JumpButton.tsx";
import { TrailBlock } from "./trail/TrailBlock.tsx";

interface UnitHeaderProps {
	u: UnitView;
	prev: UnitView | undefined;
	next: UnitView | undefined;
	st: GameState;
	onReviewKeys?: (keys: ProgressKey[]) => void;
}

function UnitHeader({ u, prev, next, st, onReviewKeys }: UnitHeaderProps) {
	const t = useStrings();
	const { tagKey, icon: TagIcon } = TRACK_UI[u.track];
	const { unlocked, due, complete, note } = unitHeader(st, u, prev, next);
	return (
		<div
			className="mt-9 rounded-3xl p-4"
			style={{ background: COL.card, border: `2px solid ${COL.line}`, fontFamily: UI }}
		>
			<div className="flex items-baseline justify-between gap-2">
				<div
					className="flex items-center gap-1.5 text-sm font-semibold"
					style={{ color: COL.mute }}
				>
					<TagIcon size={14} aria-hidden />
					{t("home.unitOrdinal", { n: u.number })} · {t(tagKey)}
				</div>
				<div className="flex items-center gap-2">
					{unlocked && due.length > 0 && onReviewKeys && (
						<button
							type="button"
							onClick={() => onReviewKeys(due)}
							className={`rounded-full px-3 py-1 text-xs font-extrabold ${FOCUS}`}
							style={{ background: COL.lapisSoft, color: COL.lapisDark }}
						>
							{t("home.reviewDue", { n: due.length })}
						</button>
					)}
					{complete && <Crown size={18} color={COL.apricotDark} />}
				</div>
			</div>
			<div className="text-lg leading-tight font-extrabold">{u.title}</div>
			<p className="mt-1 text-sm leading-relaxed" style={{ color: COL.mute }}>
				{u.blurb}
			</p>
			{u.chips.length > 0 && (
				<div
					className="mt-3 grid gap-2"
					style={{ gridTemplateColumns: `repeat(${chipColumns(u.chips.length)}, minmax(0, 1fr))` }}
				>
					{u.chips.map((c) => (
						<div
							key={c.itemId}
							className="flex flex-col items-center rounded-2xl py-2"
							style={{ background: COL.tint }}
						>
							<FaceView face={c.face} size={26} />
						</div>
					))}
				</div>
			)}
			{note && (
				<p className="mt-3 text-sm" style={{ color: COL.mute }}>
					{t(note.key, note.params)}
				</p>
			)}
		</div>
	);
}

interface UnitBlockProps {
	hu: HomeUnit;
	st: GameState;
	width: number;
	/** Where a focus request for the learner's place lands: the place's node. */
	focusRef?: Ref<HTMLDivElement>;
	/** The current lesson's node, for the jump button. */
	currentRef?: Ref<HTMLDivElement>;
	/** Where a focus request for this unit lands. */
	blockRef?: Ref<HTMLDivElement>;
	onLesson: (u: UnitView, lesson: LessonDef) => void;
	onReviewKeys?: (keys: ProgressKey[]) => void;
}

function UnitBlock({
	hu,
	st,
	width,
	focusRef,
	currentRef,
	blockRef,
	onLesson,
	onReviewKeys,
}: UnitBlockProps) {
	const { unit: u, prev, next } = hu;
	return (
		// Clears the sticky header when a unit focus scrolls the block to the top.
		<div ref={blockRef} data-unit-block={u.id} style={{ scrollMarginTop: 72 }}>
			<UnitHeader u={u} st={st} prev={prev} next={next} onReviewKeys={onReviewKeys} />
			<TrailBlock
				hu={hu}
				st={st}
				width={width}
				focusRef={focusRef}
				currentRef={currentRef}
				onLesson={onLesson}
			/>
		</div>
	);
}

export type { FocusRequest };

interface HomeProps {
	st: GameState;
	onLesson: (u: UnitView, lesson: LessonDef) => void;
	/** A pending request to scroll to the learner's place. See AppLayout's `focus`. */
	focus?: FocusRequest | null;
	onFocused?: () => void;
	/** Starts a review of exactly the keys it is given. Optional: a consumer that
	    never starts reviews can leave it out, and the badge then does not render. */
	onReviewKeys?: (keys: ProgressKey[]) => void;
	isOffline?: (u: UnitView, l: LessonDef) => boolean;
	/** A tap on a place card, by its stop's id. */
	onOpenPlace?: (stopId: string) => void;
	/** A tap on a done stop's Polaroid, by its stop's id. */
	onOpenPhoto?: (stopId: string) => void;
	/** Where the photo dates come from: only this device's own finishes are dated. */
	polaroids?: PolaroidMemory | null;
	/** The stops whose card plays its reveal now. */
	revealing?: ReadonlySet<string>;
	/** The jump button took the learner back to their lesson. */
	onJump?: (direction: "up" | "down") => void;
}

export function Home({
	st,
	onLesson,
	focus,
	onFocused,
	onReviewKeys,
	isOffline,
	onOpenPlace,
	onOpenPhoto,
	polaroids,
	revealing,
	onJump,
}: HomeProps) {
	const course = useCourse();
	const t = useStrings();
	const sections = homeSections(course.views.units(), st, isOffline);
	const cards = placeCardsOf(sections, st);
	const focusRef = useRef<HTMLDivElement | null>(null);
	// State, not a ref: the jump button must observe the node once it mounts.
	const [currentEl, setCurrentEl] = useState<HTMLElement | null>(null);
	const columnRef = useRef<HTMLDivElement | null>(null);
	// The phone layout until measured, and wherever there is no ResizeObserver (tests).
	const [width, setWidth] = useState(MIN_WIDTH + 70);
	const hasCurrent = sections.some((s) =>
		s.units.some((hu) => hu.lessons.some((l) => l.isCurrent)),
	);

	useLayoutEffect(() => {
		const el = columnRef.current;
		if (!el) return;
		const measure = () => {
			const w = el.getBoundingClientRect().width;
			if (w > 0) setWidth(Math.round(w));
		};
		measure();
		if (typeof ResizeObserver !== "function") return;
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		if (!focus) return;
		// Consume before scrolling: if the scroll throws, the request is already
		// spent and a later tab switch will not move the map.
		onFocused?.();
		const el = focusRef.current;
		if (!el) return;
		// Optional call: scrollIntoView is absent in some non-browser hosts.
		el.scrollIntoView?.({
			block: focus.unitId ? "start" : "center",
			behavior: focus.smooth && !reducedMotion() ? "smooth" : "auto",
		});
	}, [focus, onFocused]);

	const unitBlock = (hu: HomeUnit) => (
		<UnitBlock
			key={hu.unit.id}
			hu={hu}
			st={st}
			width={width}
			currentRef={setCurrentEl}
			// One target per request: a unit focus takes the ref off the current lesson.
			focusRef={focus?.unitId ? undefined : focusRef}
			blockRef={focus?.unitId === hu.unit.id ? focusRef : undefined}
			onLesson={onLesson}
			onReviewKeys={onReviewKeys}
		/>
	);

	const section = ({ section: s, entries }: HomeSection) => (
		<div key={s?.id ?? "units"}>
			{s && (
				<div className="mt-10 flex items-center gap-3">
					<div className="flex-1" style={{ height: 2, background: COL.line }} />
					<div className="text-center">
						<div className="text-base font-extrabold">{s.title}</div>
						<G size={15} style={{ color: COL.mute }}>
							{s.sub}
						</G>
					</div>
					<div className="flex-1" style={{ height: 2, background: COL.line }} />
				</div>
			)}
			{entries.map((e) => {
				if (e.kind === "unit") return unitBlock(e.unit);
				const { stop, state } = e;
				const card = cards.get(stop.id);
				const taken = polaroids?.taken[stop.id] ?? null;
				return (
					<div data-testid="stop" key={stop.id}>
						{card && (
							<PlaceCard
								model={card}
								revealing={revealing?.has(stop.id) ?? false}
								lang={st.lang}
								taken={taken}
								onOpen={() => onOpenPlace?.(stop.id)}
							/>
						)}
						{e.units.map(unitBlock)}
						<PhotoSpot
							stop={stop}
							state={state}
							taken={taken}
							lang={st.lang}
							onOpen={() => onOpenPhoto?.(stop.id)}
						/>
					</div>
				);
			})}
		</div>
	);

	return (
		<div className="px-4 pt-4 pb-32">
			<div ref={columnRef}>{sections.map(section)}</div>
			{hasCurrent && <JumpButton target={currentEl} onJump={onJump} />}
		</div>
	);
}
